import React, { useEffect, useState } from 'react'
import { QuestionSidePanelProps } from './constants/types'
import { ListType, QuestionsTabs } from './constants/enums'
import QuestionList from './QuestionList/QuestionList'
import QuestionConversationWindow from './QuestionList/QuestionConversationWindow'
import Tabs from '@bt-react/components/Tabs'
import { IconType } from '@bt-react/components/Icon'
import SidePanelFramework from '@bt-react/components/SidePanelFramework'
import EmptySidePanel from '@bt-react/components/EmptySidePanel'
import style from './questionsSidePanel.module.scss'
import classNames from 'classnames'
import { QandAQuestion } from '../../dataStore/reducers/questions/types'

const sortQuestionsByTimestamp = (arr: QandAQuestion[]) => arr.sort((a, b) =>
  Number(new Date(b.created)) - Number(new Date(a.created)))

const QuestionsSidePanel = ({ questions, questionsLoading, typing, sendMessageLoading, showAnsweredNotification,
  setAllMessagesAsRead, addReplyToConversation, handleKeyDownEvent, handleQuestionIconClick, resetSendQuestionLoadingState }: QuestionSidePanelProps) => {
  const [ hideConversationWindow, setHideConversationWindow ] = useState(true)
  const [ hideReplyOption, setHideReplyOption ] = useState(true)
  const [ selectedTab, setSelectedTab ] = useState(QuestionsTabs.Active)
  const [ selectedConversation, setSelectedConversation] = useState<undefined | QandAQuestion>()

  const handleReplyClick = (conversationUniqueName?: string | undefined | null) => {
    if (conversationUniqueName) {
      const selectedQuestion = questions.find(question => question.uniqueConversationName === conversationUniqueName)
      if (selectedQuestion) setSelectedConversation(selectedQuestion)
    }
    setHideConversationWindow(false)
    setHideReplyOption(false)
  }

  const handleCloseConversationWindowClick = () => {
    setHideConversationWindow(true)
    setHideReplyOption(false)
  }

  const setTabs = (tab: QuestionsTabs) => setSelectedTab(tab)

  useEffect(() => {

    if (selectedConversation) {

      const updatedSelectedConversation = questions.find(question => question.uniqueConversationName === selectedConversation.uniqueConversationName)
      setSelectedConversation(updatedSelectedConversation)

    }

  }, [questions, selectedConversation])

  const getTabs = () => [
    {
      tabItem:
        <div
          data-bdd='question-active-tab'
          className={classNames(style['question-side-panel__tab-item'],
          selectedTab === QuestionsTabs.Active && style['question-side-panel__tab-item--active'])}
          onClick={() => setTabs(QuestionsTabs.Active)}>
          Active ({questions.filter(question => !question.isAnswered).length})
        </div>,
      isActive: selectedTab === QuestionsTabs.Active,
      dataBdd: 'questions-tabs-active'
    },
    {
      tabItem:
        <div
          data-bdd='question-answered-tab'
          className={classNames(style['question-side-panel__tab-item'],
          selectedTab === QuestionsTabs.Answered && style['question-side-panel__tab-item--active'])}
          onClick={() => setTabs(QuestionsTabs.Answered)}>
          Answered ({questions.filter(question => question.isAnswered).length})
        </div>,
      isActive: selectedTab === QuestionsTabs.Answered,
      dataBdd: 'questions-tabs-answered'
    }
  ]

  return <SidePanelFramework
    icon={IconType.iconName.question}
    title='Questions'
    scrollable={true}
    contentPadding={false}
    content={
      <div className={
        classNames(style['question-side-panel-container'],
        !hideConversationWindow && style['question-side-panel-container__question-conversation-window--open']
      )}>
        { questions && questions.length > 0
          ? <>
              <Tabs tabs={getTabs()} />
              {
                selectedTab === QuestionsTabs.Active ? <>
                  <QuestionList
                    title='Prioritized'
                    questions={sortQuestionsByTimestamp(questions.filter(question => question.isSticky && !question.isAnswered))}
                    listType={ListType.PRIORITY}
                    handleQuestionIconClick={handleQuestionIconClick}
                    handleReplyClick={handleReplyClick} />
                  <QuestionList
                    title='Active'
                    questions={sortQuestionsByTimestamp(questions.filter(question => !question.isSticky && !question.isAnswered))}
                    listType={ListType.ACTIVE}
                    handleQuestionIconClick={handleQuestionIconClick}
                    handleReplyClick={handleReplyClick} />
                  </> :
                  <QuestionList
                    title='Answered'
                    questions={sortQuestionsByTimestamp(questions.filter(question => question.isAnswered))}
                    listType={ListType.ANSWERED}
                    handleQuestionIconClick={handleQuestionIconClick}
                    handleReplyClick={handleReplyClick} />
              }
              {
                selectedConversation &&
                <QuestionConversationWindow
                  hideConversationWindow={hideConversationWindow}
                  hideReplyOption={hideReplyOption}
                  question={selectedConversation}
                  typing={typing}
                  sendMessageLoading={sendMessageLoading}
                  showAnsweredNotification={showAnsweredNotification}
                  setAllMessagesAsRead={setAllMessagesAsRead}
                  setHideReplyOption={setHideReplyOption}
                  handleCloseClick={handleCloseConversationWindowClick}
                  addReplyToConversation={addReplyToConversation}
                  handleKeyDownEvent={handleKeyDownEvent}
                  resetSendQuestionLoadingState={resetSendQuestionLoadingState}
                />
              }
            </>
          : <EmptySidePanel
              icon={questionsLoading ? IconType.iconName.spinner : IconType.iconName.question}
              title={questionsLoading ? 'Loading questions' : 'No questions yet'}
              text={
                questionsLoading ? `This may take up to 30 seconds depending on the volume of questions.` :
                'Live questions from your audience will appear here.'
              }
            />
        }
      </div>
    }>
  </SidePanelFramework>
}

export default QuestionsSidePanel
